/**
 * Unified helper for picking the default connection when an agent variant
 * is selected from any of the pickers (edge dual-button, toolbar dock,
 * category browse, floating command bar). All four call sites need the
 * same logic:
 *
 *   • Workflow Agent (`native`): if the region has curated Microsoft-managed
 *     models, register the synthetic `managedAiConnection` in the editor
 *     store (never in the action's modelConfigurations) so the node editor
 *     lands on Parameters instead of Connection — Microsoft-managed is the
 *     default regardless of whether the workflow already has an eligible
 *     BYOM agent connection. Users who want a BYOM connection instead can
 *     still switch it from the Parameters/Connection tab after the node is
 *     created.
 *   • Managed Agent (`managed`), Foundry, and hosted variants: Microsoft-managed
 *     isn't supported yet — Managed Agent stays BYOM-only until the managed
 *     runtime path supports the GHCP harness, and Foundry/hosted have their
 *     own connection surface. These variants always fall through to the
 *     plain variant defaults.
 *
 * Callers should:
 *   1. Call `resolveAgentOperationForVariant` to produce the operation.
 *   2. If `injectedManagedRef` is true and the store doesn't already carry
 *      the managed ref, call `registerCreatedConnection(
 *      MANAGED_MODEL_REFERENCE_KEY, buildManagedModelReference())`.
 *   3. Forward the returned `operation` to the normal
 *      `onOperationSelect` / addAction path.
 *
 * Foundry and hosted variants intentionally short-circuit — they use their
 * own connection surface and don't participate in the model-connection
 * picker at all.
 */

import type { OperationDefinition } from '@/types/operations';
import type { ConnectionReferences } from '@/types/connectionReference';
import { MANAGED_MODEL_PROVIDER_ID, MANAGED_MODEL_REFERENCE_KEY } from '@/utils/connections';
import {
  buildAgentOperationFromSelection,
  type AgentVariant,
  type AgentOperationSelection,
} from './agentVariant';

export interface ResolveAgentOperationContext {
  /** Whether runtime discovery returned a non-empty curated model list.
   *  When false the helper always falls through. */
  isManagedModelActive: boolean;
  defaultManagedModelId?: string | undefined;
  /** Current connection references in the workflow store. Only read for
   *  `native` today (Microsoft-managed is its default regardless of any
   *  existing BYOM connection); retained on the context shape for variants
   *  that may need BYOM-eligibility checks in the future. */
  references: ConnectionReferences;
  /** Optional explicit connection ref chosen by the user (e.g. from Step 2
   *  of AgentTypeView). When set, the helper honours it verbatim and skips
   *  the Microsoft-managed auto-injection — the user has already made a choice. */
  explicitConnectionRefName?: string;
  /** Explicit model provider hint that pairs with `explicitConnectionRefName`
   *  (e.g. `'MicrosoftManaged'` when the user picks the managed entry
   *  from Step 2). Forwarded to `buildAgentOperationFromSelection`. */
  explicitModelProvider?: AgentOperationSelection['modelProvider'];
}

export interface ResolveAgentOperationResult {
  /** The resolved operation ready to hand to the picker / addAction. */
  operation: OperationDefinition;
  /** True when the helper selected the synthetic managed ref. Callers
   *  should ensure the ref is registered in the connection store
   *  (typically via `registerCreatedConnection`). */
  injectedManagedRef: boolean;
}

/** Variant supports the Microsoft-managed default flow at all. Only the
 *  Workflow Agent (`native`) does today — Managed Agent doesn't support
 *  Microsoft-managed yet (BYOM-only until the managed runtime path supports
 *  the GHCP harness), and Foundry / hosted variants use their own connection
 *  type and are excluded. */
const supportsManagedDefault = (variant: AgentVariant): boolean => variant === 'native';

/**
 * Resolve an `OperationDefinition` for a picked agent variant, applying
 * the Microsoft-managed default per the rules in the module comment.
 */
export const resolveAgentOperationForVariant = (
  baseOp: OperationDefinition,
  variant: AgentVariant,
  ctx: ResolveAgentOperationContext,
): ResolveAgentOperationResult => {
  // Explicit user pick short-circuits the helper — nothing to auto-decide.
  if (ctx.explicitConnectionRefName) {
    const selection: AgentOperationSelection = {
      variant,
      connectionRefName: ctx.explicitConnectionRefName,
      ...(ctx.defaultManagedModelId ? { defaultManagedModelId: ctx.defaultManagedModelId } : {}),
      ...(ctx.explicitModelProvider !== undefined
        ? { modelProvider: ctx.explicitModelProvider }
        : {}),
    };
    return {
      operation: buildAgentOperationFromSelection(baseOp, selection),
      injectedManagedRef: false,
    };
  }

  const useManaged = ctx.isManagedModelActive && supportsManagedDefault(variant);

  const selection: AgentOperationSelection = useManaged
    ? {
        variant,
        connectionRefName: MANAGED_MODEL_REFERENCE_KEY,
        modelProvider: MANAGED_MODEL_PROVIDER_ID,
        ...(ctx.defaultManagedModelId ? { defaultManagedModelId: ctx.defaultManagedModelId } : {}),
      }
    : { variant };

  return {
    operation: buildAgentOperationFromSelection(baseOp, selection),
    injectedManagedRef: useManaged,
  };
};
